import React, { useState, useContext } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import ScrollableContent from '@wix/da-ds/pkg/ScrollableContent';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import ChooseAvatarDumbo from './_partials/ChooseAvatarDumbo';
import ExclusiveAvatarButton from '../ExclusiveAvatarButton/ExclusiveAvatarButton';
import ExclusiveSpiderWeb from '../CommentBoxContentWrapper/ExclusiveSpiderWeb';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { BiContext } from '@wix/da-bi/pkg/BiLogger.context';
import {
  BiData,
  ApplyAvatarBiEvent,
  BiContextProvidedValue,
} from '@wix/da-bi/pkg/events';

import s from './ChooseAvatarModal.scss';

export interface Props {
  /** Callback when modal is closed */
  onClose?: () => void;
  /** Callback when a deviation is selected and applied */
  onSelect?: (selectedDeviation: PapiDeviation | null) => void;
  /** Callback to request loading more deviations (handled by parent) */
  onLoadMore?: () => void;
  /** List of deviations to choose from */
  deviations: PapiDeviation[];
  /** Whether the stream has been initialized */
  isInitialized: boolean;
  /** Currently selected/active deviation being used as avatar */
  currentlySelectedDeviation?: PapiDeviation | null;
  /** Distance from bottom in px to trigger onLoadMore */
  loadMoreDistance?: number;
}

/**
 * Modal for choosing avatar to comment with
 * Based on Figma design: Choose Avatar to comment
 */
const ChooseAvatarModal: React.FC<Props> = ({
  onClose,
  onSelect,
  onLoadMore,
  deviations,
  currentlySelectedDeviation,
  loadMoreDistance = 80,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { is_comment_thread } = useContext(BiContext);
  const logBiEvent = useBiLogger();
  // Initialize with currently selected deviation if it exists
  const initialSelectedId = currentlySelectedDeviation
    ? currentlySelectedDeviation.deviationId
    : undefined;
  const [selectedDeviationId, setSelectedDeviationId] = useState<
    number | null | undefined
  >(initialSelectedId);

  const handleAvatarClick = (deviationId: number) => {
    if (selectedDeviationId === deviationId) return;
    setSelectedDeviationId(deviationId);
  };

  const handleEmptyClick = () => {
    const newSelectedId = selectedDeviationId === null ? undefined : null;
    setSelectedDeviationId(newSelectedId);
  };

  const handleApply = () => {
    const hadExclusive = !!currentlySelectedDeviation;
    let newExclusiveId: number | null | undefined = undefined;

    if (selectedDeviationId === null) {
      newExclusiveId = null; // Dropping the mask
    } else if (typeof selectedDeviationId === 'number') {
      newExclusiveId = selectedDeviationId;
    }

    if (newExclusiveId !== undefined) {
      const isCommentThreadValue: 1 | 0 | null =
        typeof is_comment_thread === 'number'
          ? (is_comment_thread as 1 | 0 | null)
          : null;

      logBiEvent(
        BiData<ApplyAvatarBiEvent>({
          evid: 195,
          has_exclusive: hadExclusive ? 1 : 0,
          new_exclusiveid: newExclusiveId,
          notification_type: BiContextProvidedValue,
          notification_message_id: BiContextProvidedValue,
          typeid: BiContextProvidedValue,
          itemid: BiContextProvidedValue,
          is_comment_thread: isCommentThreadValue,
        })
      );
    }

    if (selectedDeviationId === null) {
      onSelect?.(null);
      onClose?.();
      return;
    }

    if (typeof selectedDeviationId === 'number') {
      const selectedDeviation = deviations.find(
        d => d.deviationId === selectedDeviationId
      );
      if (selectedDeviation) {
        onSelect?.(selectedDeviation);
      }
    }
    onClose?.();
  };

  const handleCancel = () => {
    setSelectedDeviationId(undefined);
    onClose?.();
  };

  const handleScrolled = (scrollTop: number, distanceToBottom: number) => {
    if (distanceToBottom < loadMoreDistance) {
      onLoadMore?.();
    }
  };

  return (
    <Modal
      isOpen
      onRequestClose={onClose}
      hasCloseButton={true}
      showHeaderBorder
      showFooterBorder
      withScrollableContentWrapper={false}
      heading={t(
        isMobile
          ? 'commentBox.exclusiveAvatars.modal.title.mobile'
          : 'commentBox.exclusiveAvatars.modal.title'
      )}
      footerRight={[
        <TextButton
          key="cancel"
          size="large"
          variant="main"
          onClick={handleCancel}
        >
          {t('common.cancel')}
        </TextButton>,
        <ExclusiveAvatarButton key="apply" onClick={handleApply}>
          {selectedDeviationId === null
            ? t('common.select')
            : t('commentBox.exclusiveAvatars.modal.wearYourMask')}
        </ExclusiveAvatarButton>,
      ]}
    >
      <ScrollableContent
        wrapperClassName={s['scrollable-wrapper']}
        className={s['content']}
        onScrolled={handleScrolled}
      >
        <p className={s['description']}>
          <Trans
            i18nKey="commentBox.exclusiveAvatars.modal.description"
            components={[
              <a
                key="link"
                href={t(
                  'commentBox.exclusiveAvatars.greetingsModal.learnMoreUrl'
                )}
                className={s['description-link']}
              />,
            ]}
          />
        </p>
        <img
          src="//st.deviantart.net/eclipse/illustrations/halloween_2026_mask.png"
          className={s['exclusive-mask-image']}
          alt="Exclusive Mask"
        />
        <ExclusiveSpiderWeb className={s['exclusive-spider-web']} />
        <div className={s['avatar-container']}>
          {currentlySelectedDeviation && (
            <button
              type="button"
              className={`${s['empty-avatar']} ${
                selectedDeviationId === null ? s['selected'] : ''
              }`}
              onClick={handleEmptyClick}
            >
              {t('commentBox.exclusiveAvatars.dropMask', 'Drop the mask')}
            </button>
          )}
          {deviations.map(deviation => (
            <ChooseAvatarDumbo
              key={deviation.deviationId}
              deviation={deviation}
              selected={selectedDeviationId === deviation.deviationId}
              onClick={() => handleAvatarClick(deviation.deviationId)}
            />
          ))}
        </div>
      </ScrollableContent>
    </Modal>
  );
};

ChooseAvatarModal.displayName = 'ChooseAvatarModal';

export default ChooseAvatarModal;
